import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowLeft, Store, Truck, Clock, Loader2, CreditCard, Banknote, Ticket } from "lucide-react";
import { useCart } from "../context/CartContext";
import { restaurant } from "../mock/menuData";
import { toast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => n.toFixed(2).replace(".", ",") + "€";

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [mode, setMode] = useState("emporter");
  const [form, setForm] = useState({ name: "", phone: "", address: "", note: "" });
  const [payment, setPayment] = useState("cb"); // cb | especes | ticket
  const [cashChoice, setCashChoice] = useState("appoint"); // appoint | 20 | 30 | 40 | 50
  const [placed, setPlaced] = useState(null);
  const [loading, setLoading] = useState(false);

  const deliveryFee = mode === "livraison" && subtotal < restaurant.freeDeliveryFrom ? 2.5 : 0;
  const total = subtotal + deliveryFee;

  const paymentLabel = payment === "cb" ? "Carte bancaire" : payment === "especes" ? "Espèces" : "Ticket Restaurant";
  const paymentDetail =
    payment === "especes"
      ? cashChoice === "appoint"
        ? "Appoint (montant exact)"
        : `Monnaie sur ${cashChoice}€`
      : payment === "ticket"
      ? "1 seul ticket resto accepté"
      : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || (mode === "livraison" && !form.address)) {
      toast({ title: "Champs manquants", description: "Merci de compléter vos coordonnées." });
      return;
    }
    setLoading(true);
    const payload = {
      items: items.map((l) => ({
        name: l.name,
        format: l.format || "seul",
        meats: l.meats || [],
        sauces: l.sauces || [],
        crudites: l.crudites || [],
        support: l.support || null,
        boisson: l.boisson || null,
        unitPrice: l.unitPrice,
        qty: l.qty,
      })),
      customer: form,
      mode,
      paymentMethod: paymentLabel,
      paymentDetail,
      subtotal,
      deliveryFee,
      total,
    };
    try {
      const { data } = await axios.post(`${API}/orders`, payload);
      setPlaced({ ref: data.ref, total, email: data.email_sent });
      clear();
      toast({ title: "Commande confirmée ✅", description: `Référence ${data.ref}` });
    } catch (err) {
      console.error("Order error:", err);
      toast({ title: "Erreur", description: "Impossible d'envoyer la commande. Réessayez." });
    } finally {
      setLoading(false);
    }
  };

  if (placed) {
    return (
      <div className="brick-bg min-h-screen pt-28 pb-20 flex items-center justify-center px-4">
        <div className="bg-[#141414] border border-white/10 rounded-3xl p-8 max-w-md text-center animate-float-up">
          <CheckCircle2 size={64} className="text-pink mx-auto" />
          <h1 className="font-display text-3xl uppercase text-white mt-4">Merci !</h1>
          <p className="text-white/60 mt-2">Votre commande <b className="text-gold">{placed.ref}</b> a bien été enregistrée.</p>
          <p className="text-white/60">Total : <b className="text-white">{fmt(placed.total)}</b></p>
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-white/50"><Clock size={15} className="text-gold" /> Prêt dans ~20 min</div>
          <div className="mt-3 text-xs text-white/40">
            {placed.email ? "Le restaurant a été notifié par email 📧" : "Commande enregistrée. Appelez le " + restaurant.phone + " pour confirmer."} — paiement sur place / à la livraison.
          </div>
          <button onClick={() => navigate("/")} className="mt-6 bg-pink text-white font-display uppercase px-7 py-3 rounded-full btn-shine">Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  return (
    <div className="brick-bg min-h-screen pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <button onClick={() => navigate("/carte")} className="flex items-center gap-2 text-white/60 hover:text-pink mb-6"><ArrowLeft size={18} /> Continuer mes achats</button>
        <h1 className="font-display text-4xl uppercase text-white mb-8">Finaliser la commande</h1>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/60">Votre panier est vide.</p>
            <button onClick={() => navigate("/carte")} className="mt-5 bg-pink text-white font-display uppercase px-6 py-2.5 rounded-full btn-shine">Voir la carte</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-5 gap-8">
            <form onSubmit={submit} className="md:col-span-3 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setMode("emporter")} className={`rounded-xl border-2 p-4 text-left transition-all ${mode === "emporter" ? "border-pink bg-pink/10" : "border-white/15"}`}>
                  <Store size={22} className="text-gold mb-1" /><span className="block font-display uppercase text-white">À emporter</span>
                  <span className="text-xs text-white/50">Retrait sur place</span>
                </button>
                <button type="button" onClick={() => setMode("livraison")} className={`rounded-xl border-2 p-4 text-left transition-all ${mode === "livraison" ? "border-pink bg-pink/10" : "border-white/15"}`}>
                  <Truck size={22} className="text-gold mb-1" /><span className="block font-display uppercase text-white">Livraison</span>
                  <span className="text-xs text-white/50">Gratuite dès {restaurant.freeDeliveryFrom}€</span>
                </button>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1.5">Nom complet *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#181818] border border-white/15 rounded-xl px-4 py-3 text-white focus:border-pink outline-none transition-colors" placeholder="Votre nom" />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1.5">Téléphone *</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#181818] border border-white/15 rounded-xl px-4 py-3 text-white focus:border-pink outline-none transition-colors" placeholder="06 XX XX XX XX" />
              </div>
              {mode === "livraison" && (
                <div>
                  <label className="block text-sm text-white/70 mb-1.5">Adresse de livraison *</label>
                  <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-[#181818] border border-white/15 rounded-xl px-4 py-3 text-white focus:border-pink outline-none transition-colors" placeholder="Adresse, code, étage..." />
                </div>
              )}
              <div>
                <label className="block text-sm text-white/70 mb-1.5">Note (optionnel)</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="w-full bg-[#181818] border border-white/15 rounded-xl px-4 py-3 text-white focus:border-pink outline-none transition-colors resize-none" placeholder="Instructions particulières" />
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2 font-display uppercase text-gold">Paiement</label>
                <div className="grid grid-cols-3 gap-3">
                  <button type="button" onClick={() => setPayment("cb")} className={`rounded-xl border-2 p-3 text-center transition-all ${payment === "cb" ? "border-pink bg-pink/10" : "border-white/15 hover:border-white/40"}`}>
                    <CreditCard size={22} className="text-gold mb-1 mx-auto" /><span className="block text-sm font-semibold text-white">Carte bancaire</span>
                  </button>
                  <button type="button" onClick={() => setPayment("especes")} className={`rounded-xl border-2 p-3 text-center transition-all ${payment === "especes" ? "border-pink bg-pink/10" : "border-white/15 hover:border-white/40"}`}>
                    <Banknote size={22} className="text-gold mb-1 mx-auto" /><span className="block text-sm font-semibold text-white">Espèces</span>
                  </button>
                  <button type="button" onClick={() => setPayment("ticket")} className={`rounded-xl border-2 p-3 text-center transition-all ${payment === "ticket" ? "border-pink bg-pink/10" : "border-white/15 hover:border-white/40"}`}>
                    <Ticket size={22} className="text-gold mb-1 mx-auto" /><span className="block text-sm font-semibold text-white">Ticket resto</span>
                  </button>
                </div>

                {payment === "especes" && (
                  <div className="mt-3 bg-[#181818] border border-white/15 rounded-xl p-3">
                    <p className="text-sm text-white/70 mb-2">Vous réglerez :</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { v: "appoint", label: "Appoint (exact)" },
                        { v: "20", label: "Sur 20€" },
                        { v: "30", label: "Sur 30€" },
                        { v: "40", label: "Sur 40€" },
                        { v: "50", label: "Sur 50€" },
                      ].map((c) => (
                        <button
                          key={c.v}
                          type="button"
                          onClick={() => setCashChoice(c.v)}
                          className={`text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors ${cashChoice === c.v ? "border-pink bg-pink/15 text-pink" : "border-white/20 text-white/80 hover:border-white/50"}`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {payment === "ticket" && (
                  <div className="mt-3 flex items-start gap-2 bg-gold/10 border border-gold/40 rounded-xl p-3 text-sm text-gold">
                    <Ticket size={16} className="mt-0.5 shrink-0" />
                    <span><b>Un seul ticket restaurant accepté par commande.</b> Le complément est à régler en carte ou en espèces sur place.</span>
                  </div>
                )}
              </div>
              <button type="submit" disabled={loading} className="w-full bg-pink text-white font-display uppercase text-lg py-3.5 rounded-full btn-shine flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <><Loader2 size={20} className="animate-spin" /> Envoi…</> : <>Confirmer • {fmt(total)}</>}
              </button>
              <p className="text-center text-xs text-white/40">Paiement sur place / à la livraison. Le restaurant reçoit chaque commande par email.</p>
            </form>

            <div className="md:col-span-2">
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 sticky top-24">
                <h3 className="font-display text-xl uppercase text-gold mb-4">Récapitulatif</h3>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {items.map((l) => (
                    <div key={l.lineId} className="flex justify-between text-sm gap-2">
                      <span className="text-white/80">{l.qty}× {l.name} <span className="text-white/40">({l.format})</span></span>
                      <span className="text-white shrink-0">{fmt(l.unitPrice * l.qty)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 mt-4 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-white/70"><span>Sous-total</span><span>{fmt(subtotal)}</span></div>
                  <div className="flex justify-between text-white/70"><span>Livraison</span><span>{deliveryFee === 0 ? "Gratuite" : fmt(deliveryFee)}</span></div>
                  <div className="flex justify-between font-display text-xl text-white pt-2"><span>Total</span><span className="text-gold">{fmt(total)}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
